import * as catCodes from '@shared-constants/cat-codes-constants';
import { CatsRecordTypeEnum } from '@shared-enums/cats.enum';
import { syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { getFirstChar, trimDecimalZeros } from '@shared-helpers/string-number-helpers';

import { ITableConfigEntity } from '../interfaces/internal-interfaces';
import { CatsRecord } from '../scripts/catsRecord';
import { Employee } from '../scripts/employee';
import { getConfigByKey } from './table-configs/table-config-helpers';

export class EmployeeCatsRecord extends CatsRecord {
  public activityCodeDescription = '';

  constructor(
    private readonly employee: Employee,
    config: ITableConfigEntity[],
  ) {
    super('emp');
    this.__config = config;
    this.__ActtypeChanged$.subscribe({
      next: (code) => {
        //billable records need to change the absence attendance type when the activity changes.
        this.AbsAttType = this.getAbsAttTypeFromActtype(code);
        const value =
          this.employee.datahub.contracts
            .findContractByServiceOrder(this.RecOrder)
            ?.getEmployeeDefault(code, this.Employeenumber) ?? '';
        if (value) this.Zmatnr = value;
      },
      error: (err) => {
        console.error('Error occurred while updating Acttype:', err);
      },
    });
    this.__RecOrderChanged$.subscribe({
      //eslint-disable-next-line complexity -- TODO: split up this logic to make this less of a complex method
      next: (number) => {
        //also fires when a record is initialized.
        this.activityCodeDescription = this.employee?.activityList?.find((al) => al.value === this.Acttype)?.description ?? '';
        const configByKey = getConfigByKey(this.__config ?? [], 'Zmatnr');
        if (configByKey) {
          configByKey.selectOptions =
            this.employee.datahub.contracts.findContractByServiceOrder(number)?.getEmployeeBillCodes() ?? [];
          if (!this.Zmatnr && this.__isNew) {
            this.Zmatnr =
              this.employee.datahub.contracts
                .findContractByServiceOrder(this.RecOrder)
                ?.getEmployeeDefault(this.Acttype, this.Employeenumber) ?? '';
          }
        }
      },
      error: (err) => {
        console.error('Error occurred while updating RecOrder', err);
      },
    });
  }

  public getRequiredObject(employee: Employee): this {
    this.Employeenumber = employee.EmpId;
    this.Workdate = employee.focusedDay?.unix ?? null;
    this.Catshours = 0;
    this.Acttype = employee.Default;
    this.AbsAttType = catCodes.STRAIGHT_TIME_ATTENDANCE_CODE;
    this.RecOrder = '';
    this.ZempCreatedBy = employee.datahub.user.getEmpId();
    this.Zmatnr = '';
    this.SendCctr = employee.CostCenter;
    this.Zrecordtype = CatsRecordTypeEnum.EmployeeBillable;
    return this;
  }

  public splitSelf(value: number): EmployeeCatsRecord {
    const newValue = trimDecimalZeros((Number(this.Catshours) - value).toFixed(2));
    this.updateProperty('Catshours', newValue);
    const newRecord = new EmployeeCatsRecord(this.employee, this.__config ?? []);
    const requiredFields = newRecord.getEmptyRequiredObject(newRecord.Zrecordtype);
    syncObjectProperties(this, requiredFields, true);
    syncObjectProperties(requiredFields, newRecord, false);
    newRecord.Catshours = value;
    return newRecord;
  }

  private getEmptyRequiredObject(type: CatsRecordTypeEnum): Partial<EmployeeCatsRecord> {
    return {
      Extdocumentno: '',
      Employeenumber: '',
      Workdate: '',
      Catshours: 0,
      Acttype: '',
      AbsAttType: '',
      RecOrder: '',
      ZempCreatedBy: '',
      Zmatnr: '',
      SendCctr: '',
      Zrecordtype: type,
    };
  }

  private getAbsAttTypeFromActtype(activity: string): string {
    //possible issue: I could see this somehow being a problem in future, because I've assumed that activity types will always begin with the number. If they are zero padded or something, this would suck.
    //also, what if a record gets misclassified and they enter an equipment activity type? this would break right? probably not gonna happen. still feels weird doing it this way.
    const firstChar = getFirstChar(activity);
    if (firstChar === '2') return catCodes.STRAIGHT_TIME_ATTENDANCE_CODE;
    if (firstChar === '5') return catCodes.OVERTIME_ATTENDANCE_CODE;
    if (firstChar === '6') return catCodes.DOUBLE_TIME_ATTENDANCE_CODE;
    return '';
  }
}
